import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert } from '../../components/Alert';
import { AuthCard } from '../../components/AuthCard';
import { ApiError } from '../../lib/api';
import { dossiers, libelleValeur, NOMS_PRODUITS, type Creation, type Produit } from '../../lib/dossiers';
import { ChampQuestion } from './ChampQuestion';
import { ChampsAdresse } from './ChampsAdresse';
import { ChampActivite, ChampFormeJuridique, ChampPays, ChampsCommune } from './ChampsReferentiels';
import { erreursParChamp, referentiels } from '../../lib/referentiels';
import { composerAdresse } from '@authsemply/shared';
import { EtapeFormules } from './EtapeFormules';
import { useSessionPortail } from './useSessionPortail';
import '../../styles/dossiers.css';

const CHAMPS_IDENTITE: { cle: string; libelle: string; pro?: boolean; type?: string }[] = [
  { cle: 'name', libelle: 'Dénomination' },
  { cle: 'siret', libelle: 'SIRET (14 chiffres)', pro: true },
  // Code APE, forme juridique, pays, code postal et ville : saisis par
  // recherche dans les référentiels (14/10/2026) — cf. <ChampsReferentiels />.
  { cle: 'apeCode', libelle: 'Code APE', pro: true },
  { cle: 'legalForm', libelle: 'Forme juridique', pro: true },
  { cle: 'vatNumber', libelle: 'N° de TVA intracommunautaire', pro: true },
  { cle: 'email', libelle: 'E-mail du dossier', type: 'email' },
  // Adresse en zones (13/10/2026) : rendue par <ChampsAdresse />.
  { cle: '__adresse', libelle: 'Adresse' },
  { cle: 'country', libelle: 'Pays' },
  // Rend le code postal ET la ville (un choix remplit les deux).
  { cle: 'postCode', libelle: 'Code postal et ville' },
];

/**
 * Le parcours de création d'un dossier (11/10/2026) : formules, identité,
 * questionnaire (tronc commun puis App, Compta, Immo — ou Previ), récapitulatif,
 * création dans chaque produit. Les réponses sont enregistrées à chaque étape :
 * on peut quitter et reprendre (brouillon gardé 7 jours).
 */
export function CreationDossierPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { pret } = useSessionPortail();
  const [creation, setCreation] = useState<Creation | null>(null);
  const [etape, setEtape] = useState<string>('');
  const [reponses, setReponses] = useState<Record<string, unknown>>({});
  const [identite, setIdentite] = useState<Record<string, string>>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreursChamps, setErreursChamps] = useState<Record<string, string>>({});
  const [envoi, setEnvoi] = useState(false);
  const [recherche, setRecherche] = useState<{ enCours: boolean; message: string | null }>({ enCours: false, message: null });

  /** Pré-remplit l'identité depuis l'annuaire ; ne remplace que les champs vides, sauf le nom. */
  const chercherSiret = async () => {
    const siret = (identite.siret ?? '').replace(/[\s.]/g, '');
    if (!/^\d{14}$/.test(siret)) {
      setRecherche({ enCours: false, message: 'Saisissez un SIRET de 14 chiffres.' });
      return;
    }
    setRecherche({ enCours: true, message: null });
    try {
      const f = await dossiers.etablissement(siret);
      setIdentite((i) => ({
        ...i,
        siret,
        name: f.denomination ?? i.name ?? '',
        apeCode: i.apeCode || (f.apeCode ?? ''),
        apeLabel: i.apeCode ? (i.apeLabel ?? '') : (f.apeLibelle ?? ''),
        legalForm: i.legalForm || (f.formeJuridique ?? ''),
        legalFormCode: i.legalForm ? (i.legalFormCode ?? '') : (f.formeJuridiqueCode ?? ''),
        country: i.country || 'FR',
        // Adresse en zones, telle que SIRENE la donne (13/10/2026).
        ...(i.addressStreet || i.addressLieuDit
          ? {}
          : {
              addressNumber: f.numeroVoie ?? '',
              addressIndice: f.indiceRepetition ?? '',
              addressStreetType: f.typeVoie ?? '',
              addressStreet: f.libelleVoie ?? '',
              addressComplement: i.addressComplement || (f.complementAdresse ?? ''),
            }),
        postCode: i.postCode || (f.codePostal ?? ''),
        city: i.city || (f.ville ?? ''),
      }));
      setRecherche({
        enCours: false,
        message: f.actif ? 'Identité pré-remplie : vérifiez-la avant de continuer.' : 'Attention : cet établissement est fermé.',
      });
    } catch (e) {
      setRecherche({ enCours: false, message: e instanceof ApiError ? e.message : 'Recherche impossible pour le moment.' });
    }
  };

  const appliquer = useCallback((c: Creation, garderEtape = false) => {
    setCreation(c);
    setReponses((r) => ({ ...c.reponses, ...r }));
    setIdentite((i) => ({ ...c.identite, ...i }));
    if (!garderEtape) {
      const ids = ['formules', ...c.etapes.map((e) => e.id), 'recapitulatif'];
      setEtape(c.statut === 'BROUILLON' || c.statut === 'PAIEMENT' ? (c.etape && ids.includes(c.etape) ? c.etape : ids[0]) : 'resultats');
    }
  }, []);

  useEffect(() => {
    if (!pret || !id) return;
    dossiers
      .lire(id)
      .then((c) => appliquer(c))
      .catch((e) => setErreur(e instanceof ApiError ? e.message : 'Création introuvable.'));
  }, [pret, id, appliquer]);

  // Nouvelle étape : on repart du HAUT de la page (13/10/2026). Sans ça, le
  // bouton « Suivant » laissait l'utilisateur au bas de l'étape suivante.
  useEffect(() => {
    if (!etape) return;
    const reduit = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduit ? 'auto' : 'smooth' });
  }, [etape]);

  // Création en échec partiel (13/10/2026) : on complète les réponses dans le
  // même brouillon puis on réessaie — sans repasser par les formules (déjà
  // réglées) ni l'identité (le dossier existe).
  const partielle = creation?.statut === 'PARTIELLE';
  // Nouveau dossier ouvert à SEmplyApp : ses exigences d'identité se
  // contrôlent au fil des pages (un dossier existant porte déjà son identité).
  const avecApp = Boolean(creation?.produits.includes('APP') && !creation.companyId);
  const etapeTva = creation?.questions.find((q) => q.cle === 'tva_subject')?.etape ?? null;
  const ordre = useMemo(
    () =>
      creation
        ? [
            ...(creation.statut === 'PARTIELLE' ? [] : ['formules']),
            ...creation.etapes.map((e) => e.id).filter((e) => creation.statut !== 'PARTIELLE' || e !== 'identite'),
            'recapitulatif',
          ]
        : [],
    [creation],
  );
  const libelles: Record<string, string> = useMemo(
    () => ({
      formules: 'Formules',
      recapitulatif: 'Récapitulatif',
      ...Object.fromEntries((creation?.etapes ?? []).map((e) => [e.id, e.libelle])),
    }),
    [creation],
  );

  /** Enregistre puis relit (les conditions d'affichage dépendent des réponses). */
  async function enregistrer(partiel?: { reponses?: Record<string, unknown>; identite?: Record<string, unknown>; etape?: string }) {
    if (!creation) return null;
    try {
      const c = await dossiers.enregistrer(creation.id, partiel ?? { reponses, identite, etape });
      appliquer(c, true);
      setErreursChamps({});
      return c;
    } catch (e) {
      if (e instanceof ApiError) setErreur(e.message);
      return null;
    }
  }

  function changer(cle: string, valeur: unknown, immediat: boolean) {
    setReponses((r) => ({ ...r, [cle]: valeur }));
    // Un choix peut faire apparaître ou disparaître des questions : on enregistre tout de suite.
    if (immediat) void enregistrer({ reponses: { [cle]: valeur } });
  }

  async function suivant() {
    if (!creation) return;
    setErreur(null);
    const i = ordre.indexOf(etape);
    const prochaine = ordre[Math.min(i + 1, ordre.length - 1)];
    // Contrôle local des obligatoires de l'étape courante.
    const manquantes: Record<string, string> = {};
    for (const q of creation.questions.filter((x) => x.etape === etape && x.obligatoire)) {
      const v = reponses[q.cle] ?? creation.valeurs[q.cle];
      if (v === undefined || v === null || v === '') manquantes[q.cle] = 'Réponse obligatoire.';
    }
    if (etape === 'identite' && !(identite.name ?? '').trim()) manquantes.name = 'Le nom du dossier est requis.';
    // Ce que SEmplyApp exige (13/10/2026) — contrôlé au changement de page,
    // et non plus seulement par App après la création du dossier.
    if (avecApp && etape === 'identite') {
      if (!/^\d{14}$/.test((identite.siret ?? '').replace(/\s+/g, ''))) {
        manquantes.siret = 'SEmplyApp nécessite un SIRET de 14 chiffres.';
      }
      if (!(identite.addressStreet ?? '').trim() && !(identite.addressLieuDit ?? '').trim()) {
        manquantes.addressStreet = 'SEmplyApp nécessite l’adresse du dossier.';
      }
      if (!(identite.postCode ?? '').trim()) manquantes.postCode = 'SEmplyApp nécessite le code postal du dossier.';
      if (!(identite.apeCode ?? '').trim()) manquantes.apeCode = 'SEmplyApp nécessite le code APE du dossier.';
    }
    // Exercice comptable cohérent (exigé par SEmplyCompta, vrai pour tous).
    const debut = reponses.exercice_debut ?? creation.valeurs.exercice_debut;
    const cloture = reponses.exercice_cloture ?? creation.valeurs.exercice_cloture;
    if (
      creation.questions.some((q) => q.etape === etape && q.cle === 'exercice_cloture') &&
      typeof debut === 'string' &&
      typeof cloture === 'string' &&
      debut &&
      cloture &&
      cloture <= debut
    ) {
      manquantes.exercice_cloture = 'La date de clôture de l’exercice doit suivre sa date de début.';
    }
    const tvaDemandee =
      etape === etapeTva && (reponses.tva_subject ?? creation.valeurs.tva_subject) !== false;
    if (avecApp && tvaDemandee && !(identite.vatNumber ?? '').trim()) {
      manquantes.vatNumber = 'SEmplyApp nécessite le numéro de TVA intracommunautaire (sauf franchise en base).';
    }
    if (Object.keys(manquantes).length > 0) {
      setErreursChamps(manquantes);
      return;
    }
    setEnvoi(true);
    // Identité contrôlée contre les référentiels AVANT de quitter l'étape
    // (14/10/2026) : code postal et ville, code APE, forme juridique, pays,
    // type de voie. Le serveur renvoie aussi les valeurs au format de
    // référence (libellé APE, nom de commune La Poste), reprises ici.
    let identiteFinale = identite;
    if (etape === 'identite') {
      try {
        const v = await referentiels.verifier(identite);
        if (v.problemes.length > 0) {
          setErreursChamps(Object.fromEntries(v.problemes.map((p) => [p.champ, p.message])));
          setEnvoi(false);
          return;
        }
        identiteFinale = { ...identite };
        for (const [k, val] of Object.entries(v.valeurs)) if (val !== null) identiteFinale[k] = val;
        setIdentite(identiteFinale);
      } catch {
        // Contrôle indisponible : le serveur jugera à la création.
      }
    }
    const c = await enregistrer({ reponses, identite: identiteFinale, etape: prochaine });
    setEnvoi(false);
    if (c) setEtape(prochaine);
  }

  function precedent() {
    const i = ordre.indexOf(etape);
    if (i > 0) setEtape(ordre[i - 1]);
  }

  async function terminer() {
    if (!creation) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const c = await dossiers.terminer(creation.id);
      appliquer(c);
      setEtape('resultats');
    } catch (e) {
      if (e instanceof ApiError) {
        setErreur(e.message);
        if (e.code === 'exigences_produits') {
          // Un produit refuserait la création : rien n'a été créé. On renvoie
          // à l'étape qui porte le premier champ en cause (13/10/2026).
          const champs = (Array.isArray(e.details?.champs) ? (e.details.champs as string[]) : []).map((c) =>
            c === 'address' ? 'addressStreet' : c,
          );
          const premier = champs[0];
          const question = creation.questions.find((q) => q.cle === premier);
          if (question) setEtape(question.etape);
          else if (premier === 'vatNumber' && etapeTva && !ordre.includes('identite')) setEtape(etapeTva);
          else if (ordre.includes('identite')) setEtape('identite');
          setErreursChamps(Object.fromEntries(champs.map((c) => [c, e.message])));
        }
        if (e.code === 'IDENTITE_INCOHERENTE' && ordre.includes('identite')) {
          // Une valeur absente des référentiels (14/10/2026) : retour à l'identité.
          setEtape('identite');
          setErreursChamps(erreursParChamp(e.details));
        }
        if (e.code === 'questions_manquantes' && creation.manquantes.length > 0) {
          const q = creation.questions.find((x) => x.cle === creation.manquantes[0]);
          if (q) setEtape(q.etape);
        }
      } else setErreur('La création n’a pas abouti. Réessayez dans un instant.');
    } finally {
      setEnvoi(false);
    }
  }

  async function reessayer(produit: Produit) {
    if (!creation) return;
    setEnvoi(true);
    try {
      appliquer(await dossiers.reessayer(creation.id, produit));
      setEtape('resultats');
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Nouvel essai impossible.');
    } finally {
      setEnvoi(false);
    }
  }

  if (!pret || !creation) {
    return (
      <AuthCard title="Chargement…">
        <Alert message={erreur} />
      </AuthCard>
    );
  }

  // ─── Résultat ─────────────────────────────────────────────────────────────
  if (etape === 'resultats') {
    const tousOk = creation.resultats.length > 0 && creation.resultats.every((r) => r.statut === 'OK');
    return (
      <AuthCard className="dos-carte" title={tousOk ? 'Votre dossier est prêt' : 'Création en cours'}>
        <Alert message={erreur} />
        {creation.resultats.map((r) => (
          <div key={r.produit} className="dos-resultat">
            <span>
              <strong>{NOMS_PRODUITS[r.produit]}</strong>
              {r.erreur && r.statut === 'ECHEC' ? <small style={{ display: 'block' }}>{r.erreur}</small> : null}
            </span>
            {r.statut === 'OK' ? (
              <span className="ok">Ouvert</span>
            ) : r.statut === 'ECHEC' ? (
              <button type="button" className="btn btn-secondaire" disabled={envoi} onClick={() => void reessayer(r.produit)}>
                Réessayer
              </button>
            ) : (
              <span>En attente…</span>
            )}
          </div>
        ))}
        {creation.resultats.some((r) => r.statut === 'ECHEC') && ordre.length > 1 ? (
          <p className="dos-aide" style={{ marginTop: '1rem' }}>
            Une information manque ou a été refusée ? Complétez vos réponses : elles sont gardées, il suffit de
            corriger puis de réessayer.
          </p>
        ) : null}
        <div className="dos-actions">
          {creation.resultats.some((r) => r.statut === 'ECHEC') && ordre.length > 1 ? (
            <button type="button" className="btn btn-secondaire" disabled={envoi} onClick={() => setEtape(ordre[0])}>
              Compléter mes réponses
            </button>
          ) : null}
          {creation.retourUrl ? (
            <a className="btn" href={ajouterDossier(creation.retourUrl, creation.companyId)}>
              Revenir à l’application
            </a>
          ) : (
            <Link className="btn" to="/produits">
              Aller à mes applications
            </Link>
          )}
        </div>
      </AuthCard>
    );
  }

  const questionsEtape = creation.questions.filter((q) => q.etape === etape);
  const valeur = (cle: string) => (cle in reponses ? reponses[cle] : creation.valeurs[cle]);

  return (
    <AuthCard
      className="dos-carte"
      title={creation.companyId ? 'Ouvrir une application' : 'Créer votre dossier'}
      subtitle={creation.produits.map((p) => NOMS_PRODUITS[p]).join(' · ')}
    >
      <ol className="dos-etapes">
        {ordre.map((e, i) => (
          <li key={e} className={e === etape ? 'is-actif' : i < ordre.indexOf(etape) ? 'is-fait' : ''}>
            {libelles[e]}
          </li>
        ))}
      </ol>
      <Alert message={erreur} />

      {etape === 'formules' ? (
        <EtapeFormules creation={creation} onPret={() => void suivant()} onErreur={setErreur} />
      ) : null}

      {etape === 'identite' ? (
        <>
          <fieldset className="field">
            <legend>Ce dossier concerne</legend>
            <div className="dos-choix">
              {[
                { v: 'PRO', l: 'Une entreprise' },
                { v: 'PARTICULIER', l: 'Un particulier (sans SIRET)' },
              ].map((o) => (
                <label key={o.v} className={`dos-pastille${(identite.type ?? 'PRO') === o.v ? ' is-actif' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    checked={(identite.type ?? 'PRO') === o.v}
                    onChange={() => setIdentite((i) => ({ ...i, type: o.v }))}
                  />
                  {o.l}
                </label>
              ))}
            </div>
          </fieldset>
          {CHAMPS_IDENTITE.filter((c) => !c.pro || (identite.type ?? 'PRO') === 'PRO').map((c) =>
            ['apeCode', 'legalForm', 'country', 'postCode'].includes(c.cle) ? (
              (() => {
                const props = {
                  prefixe: 'id',
                  erreurs: erreursChamps,
                  requis: avecApp ? ['apeCode', 'postCode'] : [],
                  valeur: (cle: string) => identite[cle] ?? '',
                  onChange: (champs: Record<string, string>) => setIdentite((i) => ({ ...i, ...champs })),
                };
                if (c.cle === 'apeCode') return <ChampActivite key={c.cle} {...props} />;
                if (c.cle === 'legalForm') return <ChampFormeJuridique key={c.cle} {...props} />;
                if (c.cle === 'country') return <ChampPays key={c.cle} {...props} />;
                return <ChampsCommune key={c.cle} {...props} />;
              })()
            ) : c.cle === '__adresse' ? (
              <ChampsAdresse
                key={c.cle}
                prefixe="id"
                requis={avecApp}
                erreurs={erreursChamps}
                valeur={(cle) => identite[cle] ?? ''}
                onChange={(cle, v) => setIdentite((i) => ({ ...i, [cle]: v }))}
              />
            ) : (
            <div key={c.cle} className={`field${erreursChamps[c.cle] ? ' has-erreur' : ''}`}>
              <label htmlFor={`id-${c.cle}`}>
                {c.libelle}
                {avecApp && c.cle === 'siret' ? (
                  <span className="dos-requis" aria-hidden="true"> *</span>
                ) : null}
              </label>
              <input
                id={`id-${c.cle}`}
                type={c.type ?? 'text'}
                value={identite[c.cle] ?? ''}
                onChange={(e) => setIdentite((i) => ({ ...i, [c.cle]: e.target.value }))}
              />
              {c.cle === 'siret' ? (
                <>
                  <button type="button" className="btn btn-secondaire" disabled={recherche.enCours} onClick={() => void chercherSiret()}>
                    {recherche.enCours ? 'Recherche…' : 'Rechercher dans l’annuaire'}
                  </button>
                  {recherche.message ? (
                    <p className="dos-aide" role="status">
                      {recherche.message}
                    </p>
                  ) : null}
                </>
              ) : null}
              {erreursChamps[c.cle] ? <p className="dos-erreur">{erreursChamps[c.cle]}</p> : null}
            </div>
            ),
          )}
        </>
      ) : null}

      {etape !== 'identite' && etape !== 'recapitulatif' && etape !== 'formules' ? (
        <>
          {creation.reprises.length > 0 && etape === creation.etapes[0]?.id ? (
            <details className="dos-repli">
              <summary>Repris de votre dossier ({creation.reprises.length})</summary>
              {creation.reprises.map((r) => (
                <ChampQuestion
                  key={r.cle}
                  question={{ cle: r.cle, type: r.type, libelle: r.libelle, options: r.options }}
                  valeur={valeur(r.cle)}
                  onChange={(v) => changer(r.cle, v, false)}
                />
              ))}
            </details>
          ) : null}
          {questionsEtape.map((q) => (
            <ChampQuestion
              key={q.cle}
              question={q}
              valeur={valeur(q.cle) ?? (q.defaut as unknown)}
              erreur={erreursChamps[q.cle]}
              onChange={(v) => changer(q.cle, v, q.type === 'CHOIX' || q.type === 'BOOLEEN')}
            />
          ))}
          {/* N° de TVA demandé là où l'on apprend que le dossier est assujetti
              (13/10/2026) : SEmplyApp le refuse absent, sauf franchise. */}
          {avecApp && etape === etapeTva && valeur('tva_subject') !== false ? (
            <div className={`field dos-question${erreursChamps.vatNumber ? ' has-erreur' : ''}`}>
              <label htmlFor="id-vatNumber-tva">
                N° de TVA intracommunautaire<span className="dos-requis" aria-hidden="true"> *</span>
              </label>
              <p className="dos-aide">Exigé par SEmplyApp pour un dossier assujetti à la TVA (ex. FR12345678901).</p>
              <input
                id="id-vatNumber-tva"
                type="text"
                value={identite.vatNumber ?? ''}
                onChange={(e) => setIdentite((i) => ({ ...i, vatNumber: e.target.value }))}
              />
              {erreursChamps.vatNumber ? <p className="dos-erreur">{erreursChamps.vatNumber}</p> : null}
            </div>
          ) : null}
          {questionsEtape.length === 0 ? <p>Aucune question pour cette étape.</p> : null}
        </>
      ) : null}

      {etape === 'recapitulatif' ? (
        <>
          {creation.identiteRequise ? (
            <>
              <h2 className="dos-section">Votre dossier</h2>
              <ul className="dos-recap">
                {CHAMPS_IDENTITE.filter((c) => c.cle === '__adresse' || identite[c.cle]).map((c) => {
                  const texte =
                    c.cle === '__adresse'
                      ? [
                          composerAdresse({
                            numero: identite.addressNumber,
                            indice: identite.addressIndice,
                            typeVoie: identite.addressStreetType,
                            voie: identite.addressStreet,
                            lieuDit: identite.addressLieuDit,
                          }),
                          identite.addressComplement,
                        ]
                          .filter(Boolean)
                          .join(', ')
                      : c.cle === 'postCode'
                        ? [identite.postCode, identite.city].filter(Boolean).join(' ')
                        : c.cle === 'apeCode'
                          ? [identite.apeCode, identite.apeLabel].filter(Boolean).join(' — ')
                          : identite[c.cle];
                  return texte ? (
                    <li key={c.cle}>
                      <span>{c.libelle}</span>
                      <span>{texte}</span>
                    </li>
                  ) : null;
                })}
              </ul>
            </>
          ) : null}
          {creation.etapes
            .filter((e) => e.id !== 'identite')
            .map((e) => (
              <div key={e.id}>
                <h2 className="dos-section">{e.libelle}</h2>
                <ul className="dos-recap">
                  {creation.questions
                    .filter((q) => q.etape === e.id)
                    .map((q) => (
                      <li key={q.cle}>
                        <span>{q.libelle}</span>
                        <span>{libelleValeur(q, valeur(q.cle))}</span>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          {creation.manquantes.length > 0 ? (
            <Alert message={`${creation.manquantes.length} question(s) obligatoire(s) sans réponse.`} />
          ) : null}
        </>
      ) : null}

      {etape !== 'formules' ? (
        <div className="dos-actions">
          {ordre.indexOf(etape) > 0 ? (
            <button type="button" className="btn btn-secondaire" onClick={precedent} disabled={envoi}>
              Précédent
            </button>
          ) : null}
          {etape === 'recapitulatif' ? (
            <button type="button" className="btn" onClick={() => void terminer()} disabled={envoi || creation.manquantes.length > 0}>
              {envoi
                ? 'Création…'
                : partielle
                  ? 'Réessayer la création'
                  : creation.companyId
                    ? 'Ouvrir'
                    : 'Créer mon dossier'}
            </button>
          ) : (
            <button type="button" className="btn" onClick={() => void suivant()} disabled={envoi}>
              Suivant
            </button>
          )}
        </div>
      ) : null}

      <div className="links">
        <button
          type="button"
          className="adm-link"
          onClick={() => void dossiers.abandonner(creation.id).finally(() => navigate('/produits'))}
        >
          Abandonner
        </button>
        <span>Brouillon gardé jusqu’au {new Date(creation.expireLe).toLocaleDateString('fr-FR')}</span>
      </div>
    </AuthCard>
  );
}

/** Retour vers le produit, sur le nouveau dossier (`?dossier=<uuid>`). */
function ajouterDossier(url: string, companyId: string | null): string {
  if (!companyId) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('dossier', companyId);
    return u.toString();
  } catch {
    return url;
  }
}
